// src/components/NewsPage.tsx

import React, { useEffect, useState } from 'react';
import { Card, Pagination, Row, Col, Button } from 'antd';
import type { PaginationProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
const { Meta } = Card;

// 修改 NewsPage 组件接口
interface NewsPageProps {
  viewMode?: 'card' | 'article'; // 新增 viewMode prop
}
// interface NewsItem {
//   id: number;
//   title: string;
//   content: string;
//   date: string;
//   src: string;
// }

// import NewsPageImage1 from '@/assets/images/news/56056059.png';
// import NewsPageImage2 from '@/assets/images/news/47124048.png';
// import NewsPageImage3 from '@/assets/images/news/55563714.png';
// 新增文章新闻组件
const ArticleNews: React.FC<{ news: { id: number; title: string; cover_image:string | null, content: string; pub_date: string }[] }> = ({ news }) => {
  
  const navigate = useNavigate();

  const handleClick = (id: number) => {
    navigate(`/news/${id}`);
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' // 平滑滚动效果
    });
  };

  // 定义一个函数来提取第一个 p 标签及其内容
  const extractFirstTwoParagraphs = (html: string) => {
    const matches  = html.match(/<p[^>]*>(.*?)<\/p>/g);
    if (matches) {
      // 取前两个匹配结果
      return matches.slice(0, 2).join('');
    }
    return html;
  };

  // 定义一个函数来提取第一张图片的 src
  const extractFirstImageSrc = (html: string) => {
    const match = html.match(/<img[^>]+src="([^">]+)"/);
    return match ? match[1] : null;
  };

  return (
    <div className="article-news">
      {news.map((item) => (
        <div 
          key={item.id} 
          className="article-item mb-30"
          onClick={() => handleClick(item.id)}
          style={{ cursor: 'pointer' }}
        >
          {/* 使用提取的图片 src */}
          {extractFirstImageSrc(item.content) && (
            <img src={extractFirstImageSrc(item.content)!} alt={item.title} className="article-image" />
          )}
          <div className="article-content">
            <h4 className="article-title">{item.title}</h4>
            <div className="article-text" dangerouslySetInnerHTML={{ __html: extractFirstTwoParagraphs(item.content) }} />
            <p className="article-date">{new Date(item.pub_date).toLocaleDateString()}</p>
          </div>
        </div>
      ))}
    </div>
  );
};
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
const NewsPage: React.FC<NewsPageProps> = ({ viewMode = 'card' }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = viewMode === 'card' ? 4 : 10;
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const extractFirstImageSrc = (html: string) => {
    const match = html.match(/<img[^>]+src="([^">]+)"/);
    return match ? match[1] : null;
  };
  const handleClick = (id: number) => {
    navigate(`/news/${id}`);
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' // 平滑滚动效果
    });
  };
  // 分页逻辑
  const onChange: PaginationProps['onChange'] = (page) => {
    setCurrentPage(page);
    window.scrollTo({ 
      top: 0, 
      behavior: 'smooth' 
    });
  };
  
  const itemRender: PaginationProps['itemRender'] = (_, type, originalElement) => {
    if (type === 'prev') {
      return <a>{isEnglish ? 'Previous' : '上一页'}</a>;
    }
    if (type === 'next') {
      return <a>{isEnglish ? 'Next' : '下一页'}</a>;
    }
    return originalElement;
  };

  const [news, setNews] = useState<{ id: number; title: string; content: string;cover_image:string | null; pub_date: string; src: string }[]>([]);
  const [loading, setLoading] = useState(true);
  
  const currentNews = news.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const response = await axios.get<{ 
          id: number; 
          title: string;
          title_en: string;
          cover_image: string; 
          content: string; 
          content_en: string;
          pub_date: string 
        }[]>(
           `${API_BASE_URL}/news/`
        );
        // 处理数据，根据当前语言选择标题和内容
        const processedNews = response.data.map(item => ({
          id: item.id,
          title: isEnglish && item.title_en ? item.title_en : item.title,
          content: isEnglish && item.content_en ? item.content_en : item.content,
          cover_image: extractFirstImageSrc(isEnglish ? item.content_en : item.content), // 新增图片提取
          pub_date: item.pub_date,
          src: '' // 可以设置默认值，或者根据业务逻辑生成
        }));
        setNews(processedNews);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchNews();
  }, [isEnglish]); // 添加 isEnglish 作为依赖项，当语言变化时重新获取数据

  if (loading) {
    return <div>{isEnglish ? 'Loading...' : '加载中，请稍候...'}</div>;
  }

  return (
    <div className="news-page">
      {/* 根据视图模式显示不同内容 */}
      {viewMode === 'card' ? (
        <div>
          <div className="news-list">
            <Row gutter={ {xs: 8, sm: 16, md: 24} }>
              {currentNews.map((news) => (
                <Col key={news.id} span={24} md={12} lg={6}>
                  <Card className="card-image mb-20" hoverable onClick={() => handleClick(news.id)} 
                    cover={extractFirstImageSrc(news.content) ? <img alt="news" src={extractFirstImageSrc(news.content)!} /> : undefined}>
                    <Meta 
                      title={news.title} 
                      description={new Date(news.pub_date).toISOString()
                        .replace('T', ' ')
                        .replace(/\..+/, '')}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
          
          <div className='text-center mt-50 mb-30'>
            <Button color="primary" variant="outlined" className='button-normal-dark' shape="round" size="large" href="/news" style={{width: '200px'}}>
              {isEnglish ? 'View More' : '查看更多'}
            </Button>
          </div>
        </div>
      ) : (
        <ArticleNews news={currentNews} />
      )}
      {viewMode === 'article' && (
        <div className="text-center mt-30">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={news.length} // 使用从 API 获取的新闻总数
            onChange={onChange}
            itemRender={itemRender}
            style={{justifyContent: 'center'}}
          />
        </div>
      )}
    </div>
  );
};

export default NewsPage;